import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Map, View, MapBrowserEvent, Feature, MapEvent } from 'ol';
import { Geometry, Point } from 'ol/geom';
import { Tile as TileLayer, VectorImage} from 'ol/layer';
import { Vector as VectorSource, XYZ, OSM } from 'ol/source';
import { fromLonLat, toLonLat, transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import RenderFeature from 'ol/render/Feature';
import {createEmpty, extend, isEmpty} from 'ol/extent';
import { FitOptions } from 'ol/View';
import { isEqual } from 'lodash';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
    getRenderScale,
    createEmptyVectorSource,
    createEmptyVectorLayer,
    createSnowmobilingIconFeature,
    createCommonPathFeatures,
    createPathIconFeatures,
    createEndcapIconFeature,
    createCommonPoiIconFeature,
    createTemperaturePoiIconFeature,
    createCommonAoiIconFeature,
    createSnowmobilePathFeatures,
    createGpsIndicatorFeature,
    postMessage,
    createCommonAoiFeatures
} from './utils';
import { Style, Icon, Fill } from 'ol/style';
import { OBJECT_TYPE, OBJECT_ID, MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE_USER, MAPBOX_STYLE_ID, ICONS, PROPS, COLORS } from './constants';
import { FeatureType, MapType, PathCategoryId, PathDirection, PathStatus } from './enums';
import {
    MapCategories,
    MapCategoryProps,
    FeatureGroup,
} from './interfaces';
import {
    PathGpsCoordinate,
    Path,
    Paths,
    Poi,
    Pois,
    SnowmobilePaths,
    SnowmobileSubPath,
    SnowmobileSubPaths,
    GpsCoordinate,
    PathGpsCoordinates,
    PoiCategories,
    PathCategories,
    Aois,
    AoiCategories,
    AoiGpsCoordinates,
    AoiGpsCoordinate,
    Aoi
} from '@sodraberget/shared/src/interfaces';
import { getTranslatedProperty } from "../src/utils"
import './Map.css';

type MapProps = {
    style?: any;
    onMessage?: (msg: string) => void;
    onNearestFeature?: (data: any) => void;
};

const MapComponent = (props: MapProps, ref: any) => {
    const {onMessage} = props;
    //Selected language
    const selectedLanguage = useRef<string>('en');
    // Map
    const map = useRef<Map>();
    const mapRef = useRef<HTMLDivElement>(null);
    // 3D Map (Mapbox GL)
    const map3dRef = useRef<HTMLDivElement>(null);
    const map3d = useRef<mapboxgl.Map | null>(null);
    const map3dLoaded = useRef<boolean>(false);
    // Tile layers
    const hdMapLayer = useRef<TileLayer<OSM>>();
    const sateliteLayer = useRef<TileLayer<XYZ>>();
    // Tile sources
    const hdMapSource = useRef<OSM>();
    // Vector layers
    const snowmobilePathLayer = useRef<any>();
    const highlightedSnowmobilePathLayer = useRef<any>();
    const pathLayer = useRef<any>();
    const aoiLayer = useRef<any>();
    const highlightedAoiLayer = useRef<any>();
    const highlightedPathLayer = useRef<any>();
    const highlightedPathIconLayer = useRef<any>();
    const poiLayer = useRef<any>();
    const highlightedPoiLayer = useRef<any>();
    const gpsPositionLayer = useRef<any>();
    const snowmobileIconLayer = useRef<any>();
    const hikingIconLayer = useRef<any>();
    const crossCountrySkiingIconLayer = useRef<any>();
    const mountainBikingIconLayer = useRef<any>();
    const runningIconLayer = useRef<any>();
    const ridingIconLayer = useRef<any>();
    const canoeIconLayer = useRef<any>();
    const chairLiftIconLayer = useRef<any>();
    const gondolaIconLayer = useRef<any>();
    const surfaceLiftIconLayer = useRef<any>();
    const beltLiftIconLayer = useRef<any>();
    const skiSlopeIconLayer = useRef<any>();

    // Vector sources
    const aoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedAoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const snowmobilePathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedSnowmobilePathSource = useRef<VectorSource<Feature<Geometry>>>();
    const pathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPathIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const poiSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const gpsPositionSource = useRef<VectorSource<Feature<Geometry>>>();
    const snowmobileIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const hikingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const crossCountrySkiingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const mountainBikingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const runningIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const ridingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const canoeIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const chairLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const gondolaIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const surfaceLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const beltLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const skiSlopeIconSource = useRef<VectorSource<Feature<Geometry>>>();

    // Data set by calling exposed functions
    const poiCategoryData = useRef<PoiCategories>({});
    const poiData = useRef<Pois>({});
    const aoiCategoryData = useRef<AoiCategories>({});
    const aoiData = useRef<Aois>({});
    const aoiGpsData = useRef<AoiGpsCoordinates>({});
    const pathCategoryData = useRef<PathCategories>({});
    const pathData = useRef<Paths>({});
    const snowmobilePathData = useRef<SnowmobilePaths>({});
    const snowmobileSubPathData = useRef<SnowmobileSubPaths>({});
    const pathGpsData = useRef<PathGpsCoordinates>({});
    const mapType = useRef<MapType>();
    const filterData = useRef<FeatureGroup[]>();
    const focusData = useRef<FeatureGroup[]>();
    const selectedType = useRef<string | undefined>();
    const selectedId = useRef<string | undefined>();
    const highlightedType = useRef<string | undefined>();
    const highlightedId = useRef<string | undefined>();
    const gpsPositionData = useRef<GpsCoordinate>();
    const gpsIsCentered = useRef<boolean>();
    const headingData = useRef<number>();
    const pathDirection = useRef<PathDirection | undefined>();
    // Data set by internal functions
    const pathCategoryProps = useRef<MapCategoryProps>();
    const poiCategoryProps = useRef<MapCategoryProps>();
    const aoiCategoryProps = useRef<MapCategoryProps>();

    const setSelectedLanguage = (iso: string) => {
        selectedLanguage.current = iso;
    }
    
    /**
     * Exposed function to set the data for POIs
     */
    const setPois = (pois: Pois, categories: PoiCategories) => {
        if (!isEqual(poiData.current, pois) || !isEqual(poiCategoryData.current, categories)) {
            if (categories) {
                setPoiCategoryProps(categories);
            }

            poiData.current = pois;
            poiCategoryData.current = categories;
            redraw();
        }
    };

    /**
     * Exposed function to set the data for aois
     */
    const setAois = (aois: Aois, categories: AoiCategories, coordinates: AoiGpsCoordinates) => {
        if (!isEqual(aoiData.current, aois) || !isEqual(aoiCategoryData.current, categories) || !isEqual(aoiGpsData.current, coordinates)) {
            if (categories) {
                setAoiCategoryProps(categories);
            }
            aoiData.current = aois;
            aoiCategoryData.current = categories;
            aoiGpsData.current = coordinates;
            redraw();
        }
    };

    /**
     * Exposed function to set the data for paths
     */
    const setPaths = (paths: Paths, categories: PathCategories, coordinates: PathGpsCoordinates) => {
        if (!isEqual(pathData.current, paths) || !isEqual(pathCategoryData.current, categories) || !isEqual(pathGpsData.current, coordinates)) {
            if (categories) {
                setPathCategoryProps(categories);
            }

            pathData.current = paths;
            pathCategoryData.current = categories;
            pathGpsData.current = coordinates;
            redraw();
        }
    };

    /**
     * Exposed function to set the data for snowmobile paths
     */
    const setSnowmobilePaths = (paths: SnowmobilePaths, subPaths: SnowmobileSubPaths) => {
        if (!isEqual(snowmobilePathData.current, paths) || !isEqual(snowmobileSubPathData.current, subPaths)) {
            snowmobilePathData.current = paths;
            snowmobileSubPathData.current = subPaths;
        }
    };

    /**
     * Exposed function to set the type of map
     */
    const setMapType = (type: MapType) => {
        if (mapType.current !== type) {
            updateMapSources(type, true);

            mapType.current = type;
        }
    };
    
    /**
     * Exposed function to filter the rendered features on the map
     */
    const setFilters = (filters: FeatureGroup[]) => {
        console.log('[MAP DEBUG] setFilters() called', filters);
        if (!isEqual(filterData.current, filters)) {
            const hadFilters = filterData.current?.length;
            filterData.current = filters;

            redraw();
            // Only refocus when applying actual filters, not when clearing/initializing
            if (filters?.length) {
                refocus();
            }
        }
    };

    /**
     * Exposed function to focus on particular feature groups
     */
    const setFocus = (focus: FeatureGroup[], shouldRefocus: boolean = true) => {
        if (!isEqual(focusData.current, focus)) {
            focusData.current = focus;

            if (shouldRefocus) {
                refocus();
            }
        }
    };

    /**
     * Exposed function to set the selected feature on the map (feature to highlight and refocus on)
     */
    const setSelected = (type?: string, id?: string) => {
        selectedType.current = type;
        selectedId.current = id;

        // When deselecting (no type/id), only clear highlight without refocusing
        // This preserves the current zoom and position
        if (type === undefined && id === undefined) {
            setHighlighted(undefined, undefined);
            return;
        }

        // Highlight differs, render new highlight and then refocus
        if (highlightedType.current !== type || highlightedId.current !== id) {
            setHighlighted(type, id);
            setTimeout(() => {
                refocus();
            });
        }
        // Highlight doesn't change, just refocus
        else {
            refocus();
        }
    };

    /**
     * Exposed function to set the highlighted feature on the map
     */
    const setHighlighted = (type?: string, id?: string) => {
        //set opacity 0.5 to all unselcted features
        if (highlightedType.current !== type || highlightedId.current !== id) {
            highlightedType.current = type;
            highlightedId.current = id;
            const defaultOpacity = type !== undefined || id !== undefined ? 0.5: 1;
            //path icons
            snowmobileIconLayer.current?.setOpacity(defaultOpacity);
            snowmobileIconLayer.current?.changed();
            hikingIconLayer.current?.setOpacity(defaultOpacity);
            hikingIconLayer.current?.changed();
            mountainBikingIconLayer.current?.setOpacity(defaultOpacity);
            mountainBikingIconLayer.current?.changed();
            crossCountrySkiingIconLayer.current?.setOpacity(defaultOpacity);
            crossCountrySkiingIconLayer.current?.changed(); 
            runningIconLayer.current?.setOpacity(defaultOpacity);
            runningIconLayer.current?.changed(); 
            ridingIconLayer.current?.setOpacity(defaultOpacity);
            ridingIconLayer.current?.changed();
            canoeIconLayer.current?.setOpacity(defaultOpacity);
            canoeIconLayer.current?.changed();
            chairLiftIconLayer.current?.setOpacity(defaultOpacity);
            chairLiftIconLayer.current?.changed();
            gondolaIconLayer.current?.setOpacity(defaultOpacity);
            gondolaIconLayer.current?.changed();
            surfaceLiftIconLayer.current?.setOpacity(defaultOpacity);
            surfaceLiftIconLayer.current?.changed();
            beltLiftIconLayer.current?.setOpacity(defaultOpacity);
            beltLiftIconLayer.current?.changed();
            skiSlopeIconLayer.current?.setOpacity(defaultOpacity);
            skiSlopeIconLayer.current?.changed();
            //paths layer
            pathLayer.current?.setOpacity(defaultOpacity);
            pathLayer.current?.changed();
            snowmobilePathLayer.current?.setOpacity(defaultOpacity);
            snowmobilePathLayer.current?.changed();
            //aois layer
            aoiLayer.current?.setOpacity(defaultOpacity);
            aoiLayer.current?.changed();
            //pois layer
            poiLayer.current?.setOpacity(defaultOpacity);
            poiLayer.current?.changed();

            redrawHighlight();
        }
    };

    /**
     * Exposed function to set the current GPS position and heading on the map
     */
    const setGpsPosition = (position?: GpsCoordinate, heading?: number) => {
        if (!isEqual(gpsPositionData.current, position) || !isEqual(headingData.current, heading)) {
            gpsPositionSource.current?.clear();
          
            if (position) {
                const {longitude, latitude} = position;
                const coordinate = fromLonLat([longitude, latitude]);
                const point = new Point(coordinate);
          
                gpsPositionSource.current?.addFeatures([createGpsIndicatorFeature(point, heading)]);
                reportNearestFeature(coordinate);
            }

            // Invalidate centered state if position has changed
            if (!isEqual(gpsPositionData.current, position)) {
                gpsIsCentered.current = false;
                const msg = JSON.stringify({centered: false});
                postMessage(msg);
                onMessage?.(msg);

                gpsPositionData.current = position;
            }

            headingData.current = heading;

            // Update 3D GPS marker (with heading for rotation)
            if (mapType.current === MapType.ThreeD && map3d.current && map3dLoaded.current && position) {
                const headingDeg = heading != null ? (heading * 180 / Math.PI) : 0;
                const gpsGJ: any = {
                    type: 'FeatureCollection',
                    features: [{ type: 'Feature', properties: { heading: headingDeg }, geometry: { type: 'Point', coordinates: [position.longitude, position.latitude] } }]
                };
                if (map3d.current.getSource('gps-source')) {
                    (map3d.current.getSource('gps-source') as mapboxgl.GeoJSONSource).setData(gpsGJ);
                }
            }
        }
    };

    /**
     * Exposed function to center the view on the current GPS position
     */
    const centerGps = () => {
        // 3D mode
        if (mapType.current === MapType.ThreeD && map3d.current && gpsPositionData.current) {
            map3d.current.flyTo({
                center: [gpsPositionData.current.longitude, gpsPositionData.current.latitude],
                duration: 500,
            });
            gpsIsCentered.current = true;
            const msg = JSON.stringify({centered: true});
            postMessage(msg);
            onMessage?.(msg);
            return;
        }

        if (!gpsPositionSource.current?.getFeatures().length) {
            return;
        }
        const view = map.current?.getView();
        view?.fit(gpsPositionSource.current.getExtent());
        view?.setRotation(0);

        gpsIsCentered.current = true;

        const msg = JSON.stringify({centered: true});
        postMessage(msg);
        onMessage?.(msg);
    };

    /**
     * Exposed function to zoom in or out by a specified amount
     */
    const adjustZoom = (amount: number) => {
        const view = map.current?.getView();
        if (view === undefined) {
            return;
        }

        const zoom = view.getZoom();
        if (zoom === undefined) {
            return;
        }

        view.animate({
            zoom: zoom + amount,
            duration: 100
        });
    };

    /**
     * Exposed function to set the path direction indication
     */
    const setPathDirection = (direction?: PathDirection, redraw?: boolean) => {
        pathDirection.current = direction;

        if (redraw && highlightedType.current !== undefined && highlightedId.current !== undefined) {
            redrawHighlight();
        }
    };

    /**
     * Exposed function to center the map on a given coordinate and zoom level
     */
    const setCenter = (longitude: number, latitude: number, zoom?: number) => {
        // 3D mode
        if (mapType.current === MapType.ThreeD && map3d.current) {
            map3d.current.jumpTo({
                center: [longitude, latitude],
                zoom: zoom ?? map3d.current.getZoom(),
            });
            return;
        }

        const view = map.current?.getView();
        if (!view) return;
        view.animate({
            center: fromLonLat([longitude, latitude]),
            zoom: zoom ?? view.getZoom(),
            duration: 0,
        });
    };

    // Expose functions through the component reference
    /**
     * Updates the temperature for temperature POIs
     */
    const updateTemperatures = (temperatures: Array<{id: string, temperature: number}>) => {
        if (!poiSource.current) return;
        
        temperatures.forEach(({id, temperature}) => {
            // Update POI data
            if (poiData.current[id]) {
                (poiData.current[id] as any).temperature = temperature;
            }
        });
        
        // Redraw to update display
        redraw();
    };

    useImperativeHandle(ref, () => ({
        setAois,
        setPois,
        setPaths,
        setSnowmobilePaths,
        setMapType,
        setFilters,
        setFocus,
        setSelected,
        setHighlighted,
        setGpsPosition,
        centerGps,
        adjustZoom,
        setCenter,
        setPathDirection,
        setSelectedLanguage,
        updateTemperatures,
    }));

    // Expose functions through the window scope
    (window as any).setAois = setAois;
    (window as any).setPois = setPois;
    (window as any).setPaths = setPaths;
    (window as any).setSnowmobilePaths = setSnowmobilePaths;
    (window as any).setMapType = setMapType;
    (window as any).setFilters = setFilters;
    (window as any).setFocus = setFocus;
    (window as any).setSelected = setSelected;
    (window as any).setHighlighted = setHighlighted;
    (window as any).setGpsPosition = setGpsPosition;
    (window as any).centerGps = centerGps;
    (window as any).adjustZoom = adjustZoom;
    (window as any).setCenter = setCenter;
    (window as any).setPathDirection = setPathDirection;
    (window as any).setSelectedLanguage = setSelectedLanguage;
    (window as any).updateTemperatures = updateTemperatures;

    // Initial setup
    useEffect(() => {
        // Create Standard Definition map source (lantmäteriet)
        hdMapSource.current = new XYZ({
            //url: `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_USER}/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}`,
            // To use for retina displays (@2x) tiles för retina-displays
            url: `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_USER}/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`,
            tilePixelRatio: 2, // important to use if you use @2x URL

            attributions: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
            maxZoom: 22, // Mapbox styles only support up to 22 zoom level
            tileSize: 256, // Standard for Mapbox raster tiles.
        });

        hdMapLayer.current = new TileLayer({
            source: hdMapSource.current,
            opacity: 1.0, // Keep or adjust transparency as needed
        });
        // Set hdMapLayer to visible if the default MapType is 'Map', otherwise set its visibility to false.
        // If mapType.current is initialized as MapType.Map, make hdMapLayer visible immediately.
        hdMapLayer.current?.setVisible(mapType.current === MapType.Map);

        // Create a layer for the satelite map
        sateliteLayer.current = new TileLayer({
            source: new XYZ({
                attributions: [
                    'Powered by Esri',
                    'Source: Esri, DigitalGlobe, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community',
                ],
                attributionsCollapsible: false,
                url: `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}`,
                maxZoom: 17,
            }),
            opacity: 0.7,
        });
        sateliteLayer.current?.setVisible(false);

        // Zoom thresholds for icon visibility
        const PATH_ICON_MIN_ZOOM = 15;
        const POI_MIN_ZOOM = 13;
        const POI_TEXT_MIN_ZOOM = 16; // POI names visible at higher zoom
        const TEMPERATURE_MIN_ZOOM = 12; // Temperature POIs visible earlier than other POIs
        const ENDCAP_MIN_ZOOM = 15; // Start/end markers visible at zoom >= 15

        // Create shared style function for path layers (handles both lines and endcap icons)
        // This function hides endcap icons when zoomed out but always shows path lines
        const createPathLayerStyleFunction = () => (feature: any, resolution: number) => {
            // Check if this feature has a __baseStyle (endcap icon)
            const baseStyle = feature.get('__baseStyle');
            
            if (baseStyle) {
                // This is an endcap icon - apply zoom threshold
                const view = map.current?.getView();
                const zoom = view?.getZoom();

                if (zoom === undefined || zoom < ENDCAP_MIN_ZOOM) {
                    return null; // Hide endcap when zoomed out
                }
                
                // Show endcap when zoomed in
                if (typeof baseStyle === 'function') {
                    try {
                        return baseStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return baseStyle;
            }

            // This is a path line or other feature - check for explicit style
            const featStyle = feature.getStyle?.();
            if (featStyle) {
                if (typeof featStyle === 'function') {
                    try {
                        return featStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return featStyle;
            }

            // Use default rendering
            return null;
        };

        // Create a new layers to render snowmobile paths on
        snowmobilePathSource.current = createEmptyVectorSource();
        snowmobilePathLayer.current = createEmptyVectorLayer(snowmobilePathSource.current, createPathLayerStyleFunction());
        highlightedSnowmobilePathSource.current = createEmptyVectorSource();
        highlightedSnowmobilePathLayer.current = createEmptyVectorLayer(highlightedSnowmobilePathSource.current, createPathLayerStyleFunction());

        // Create a new layers to render paths on
        pathSource.current = createEmptyVectorSource();
        pathLayer.current = createEmptyVectorLayer(pathSource.current, createPathLayerStyleFunction());
        highlightedPathSource.current = createEmptyVectorSource();
        highlightedPathLayer.current = createEmptyVectorLayer(highlightedPathSource.current, createPathLayerStyleFunction());

        // Create a new layers to render aois on
        aoiSource.current = createEmptyVectorSource();
        aoiLayer.current = createEmptyVectorLayer(aoiSource.current);
        highlightedAoiSource.current = createEmptyVectorSource();
        highlightedAoiLayer.current = createEmptyVectorLayer(highlightedAoiSource.current);

        // Create shared style function for POI layers
        // This function hides POI icons when zoomed out and renders them when zoomed in
        const createPoiStyleFunction = () => (feature: any, resolution: number) => {
            const view = map.current?.getView();
            const zoom = view?.getZoom();

            // DEBUG: Log ~1% of style calls
            const shouldLog = Math.random() < 0.01;
            if (shouldLog) {
                console.log('[POI DEBUG] Current zoom:', zoom, 'POI_MIN_ZOOM:', POI_MIN_ZOOM);
            }

            // If zoom is not yet available, don't render
            if (zoom === undefined) {
                if (shouldLog) console.log('[POI DEBUG] Zoom undefined, returning null');
                return null;
            }

            // Check if this is a temperature POI (has __isTemperature property)
            const isTemperature = feature.get('__isTemperature');
            const minZoom = isTemperature ? TEMPERATURE_MIN_ZOOM : POI_MIN_ZOOM;

            // Hide POI icons when zoomed out
            if (zoom < minZoom) {
                if (shouldLog) console.log('[POI DEBUG] Zoom', zoom, '< minZoom', minZoom, '-> hiding POI');
                return null;
            }

            if (shouldLog) console.log('[POI DEBUG] Zoom', zoom, '>= POI_MIN_ZOOM', POI_MIN_ZOOM, '-> showing POI');

            // Get the stored base style from feature property
            const baseStyle = feature.get('__baseStyle');
            if (baseStyle) {
                if (typeof baseStyle === 'function') {
                    try {
                        return baseStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                
                // baseStyle is now [backplate, text, icon]
                // If zoom is below POI_TEXT_MIN_ZOOM, show only backplate + icon (skip text at index 1)
                if (Array.isArray(baseStyle) && zoom < POI_TEXT_MIN_ZOOM) {
                    // Return backplate (index 0) and icon (index 2), skip text (index 1)
                    return [baseStyle[0], baseStyle[2]];
                }
                
                return baseStyle;
            }

            // Fallback: check for explicit style set via setStyle()
            const featStyle = feature.getStyle?.();
            if (featStyle) {
                if (typeof featStyle === 'function') {
                    try {
                        return featStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return featStyle;
            }

            // Otherwise return null to use default rendering
            return null;
        };

        // Create a new layers to render pois on
        poiSource.current = createEmptyVectorSource();
        poiLayer.current = createEmptyVectorLayer(poiSource.current, createPoiStyleFunction());
        highlightedPoiSource.current = createEmptyVectorSource();
        highlightedPoiLayer.current = createEmptyVectorLayer(highlightedPoiSource.current, createPoiStyleFunction());

        // Create a new layer to render GPS poisition on
        gpsPositionSource.current = createEmptyVectorSource();
        gpsPositionLayer.current = createEmptyVectorLayer(gpsPositionSource.current);

        // Cache for rounded-rect backplate canvases keyed by "color|size"
        const backplateCanvasCache: Record<string, HTMLCanvasElement> = {};

        const createRoundedRectCanvas = (size: number, borderColor: string, opacity: number): HTMLCanvasElement => {
            const cacheKey = `${borderColor}|${size}|${opacity}`;
            if (backplateCanvasCache[cacheKey]) return backplateCanvasCache[cacheKey];

            const canvasSize = size * 2;
            const canvas = document.createElement('canvas');
            canvas.width = canvasSize;
            canvas.height = canvasSize;
            const ctx = canvas.getContext('2d')!;
            const borderWidth = Math.max(2, size * 0.06);
            const radius = size * 0.35; // corner radius
            const inset = borderWidth / 2;

            ctx.globalAlpha = opacity;

            // Rounded rect path
            ctx.beginPath();
            ctx.moveTo(inset + radius, inset);
            ctx.lineTo(canvasSize - inset - radius, inset);
            ctx.arcTo(canvasSize - inset, inset, canvasSize - inset, inset + radius, radius);
            ctx.lineTo(canvasSize - inset, canvasSize - inset - radius);
            ctx.arcTo(canvasSize - inset, canvasSize - inset, canvasSize - inset - radius, canvasSize - inset, radius);
            ctx.lineTo(inset + radius, canvasSize - inset);
            ctx.arcTo(inset, canvasSize - inset, inset, canvasSize - inset - radius, radius);
            ctx.lineTo(inset, inset + radius);
            ctx.arcTo(inset, inset, inset + radius, inset, radius);
            ctx.closePath();

            // White fill
            ctx.fillStyle = 'white';
            ctx.fill();

            // Colored border
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = borderWidth;
            ctx.stroke();

            backplateCanvasCache[cacheKey] = canvas;
            return canvas;
        };

        // Create shared style function for all path icon layers
        // This function hides icons when zoomed out and renders them when zoomed in
        const createPathIconStyleFunction = () => (feature: any, resolution: number) => {
            const view = map.current?.getView();
            const zoom = view?.getZoom();

            // If zoom is not yet available, don't render
            if (zoom === undefined) return null;

            // Hide icons when zoomed out
            if (zoom < PATH_ICON_MIN_ZOOM) return null;

            // Otherwise build a style from icon properties placed on the feature
            const src = feature.get && (feature.get('iconSrc') || feature.get('icon'));
            if (!src) return null;

            const scale = feature.get('iconScale') ?? getRenderScale() * 0.7;
            const anchor = feature.get('iconAnchor') ?? [0.5, 0.5];
            const anchorUnits = feature.get('iconAnchorUnits') ?? 'fraction';
            const opacity = feature.get('iconOpacity') ?? 1;
            const iconColor = feature.get('iconColor') || '#3b82f6';

            // Rounded-rect backplate: white fill + colored border
            const backplateSize = 36;
            const pixelSize = Math.round(backplateSize * scale * 2);
            const backplateCanvas = createRoundedRectCanvas(backplateSize * scale, iconColor, opacity);
            const backplateStyle = new Style({
                image: new Icon({
                    img: backplateCanvas,
                    size: [pixelSize, pixelSize],
                    scale: 1,
                    anchor: [0.5, 0.5],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                    opacity: 1, // opacity already baked into canvas
                }),
            });

            // The icon itself, tinted with the path color (scaled down relative to backplate)
            const iconScale = scale * 0.585;
            const iconStyle = new Style({
                image: new Icon({
                    src,
                    scale: iconScale,
                    anchor,
                    anchorXUnits: anchorUnits,
                    anchorYUnits: anchorUnits,
                    opacity,
                    color: iconColor || undefined,
                }),
            });

            return [backplateStyle, iconStyle];
        };

        // Create a new layer to render snowmobile icons (no clustering) - visibility controlled by zoom
        snowmobileIconSource.current = createEmptyVectorSource();
        snowmobileIconLayer.current = createEmptyVectorLayer(snowmobileIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render highlighted path icons
        highlightedPathIconSource.current = createEmptyVectorSource();
        highlightedPathIconLayer.current = createEmptyVectorLayer(highlightedPathIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render hiking icons (no clustering) - visibility controlled by zoom
        hikingIconSource.current = createEmptyVectorSource();
        hikingIconLayer.current = createEmptyVectorLayer(hikingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render cross country skiing icons (no clustering) - visibility controlled by zoom
        crossCountrySkiingIconSource.current = createEmptyVectorSource();
        crossCountrySkiingIconLayer.current = createEmptyVectorLayer(crossCountrySkiingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render mountain biking icons (no clustering) - visibility controlled by zoom
        mountainBikingIconSource.current = createEmptyVectorSource();
        mountainBikingIconLayer.current = createEmptyVectorLayer(mountainBikingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render running icons (no clustering) - visibility controlled by zoom
        runningIconSource.current = createEmptyVectorSource();
        runningIconLayer.current = createEmptyVectorLayer(runningIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render riding icons (no clustering) - visibility controlled by zoom
        ridingIconSource.current = createEmptyVectorSource();
        ridingIconLayer.current = createEmptyVectorLayer(ridingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render canoe icons (no clustering) - visibility controlled by zoom
        canoeIconSource.current = createEmptyVectorSource();
        canoeIconLayer.current = createEmptyVectorLayer(canoeIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render chair lift icons (no clustering) - visibility controlled by zoom
        chairLiftIconSource.current = createEmptyVectorSource();
        chairLiftIconLayer.current = createEmptyVectorLayer(chairLiftIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render gondola icons (no clustering) - visibility controlled by zoom
        gondolaIconSource.current = createEmptyVectorSource();
        gondolaIconLayer.current = createEmptyVectorLayer(gondolaIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render surface lift icons (no clustering) - visibility controlled by zoom
        surfaceLiftIconSource.current = createEmptyVectorSource();
        surfaceLiftIconLayer.current = createEmptyVectorLayer(surfaceLiftIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render belt lift icons (no clustering) - visibility controlled by zoom
        beltLiftIconSource.current = createEmptyVectorSource();
        beltLiftIconLayer.current = createEmptyVectorLayer(beltLiftIconSource.current, createPathIconStyleFunction());
        
        //Create a new layer to render ski slope icons (no clustering) - visibility controlled by zoom
        skiSlopeIconSource.current = createEmptyVectorSource();
        skiSlopeIconLayer.current = createEmptyVectorLayer(skiSlopeIconSource.current, createPathIconStyleFunction());

        // Create the OL Map
        map.current = new Map({
            target: mapRef.current!,
            layers: [
                hdMapLayer.current,
                sateliteLayer.current,
                snowmobilePathLayer.current,
                pathLayer.current,
                aoiLayer.current,
                snowmobileIconLayer.current,
                hikingIconLayer.current,
                mountainBikingIconLayer.current,
                crossCountrySkiingIconLayer.current,
                runningIconLayer.current,
                ridingIconLayer.current,
                canoeIconLayer.current,
                chairLiftIconLayer.current,
                gondolaIconLayer.current,
                surfaceLiftIconLayer.current,
                beltLiftIconLayer.current,
                highlightedSnowmobilePathLayer.current,
                highlightedPathLayer.current,
                highlightedPathIconLayer.current,
                highlightedAoiLayer.current,
                poiLayer.current,
                highlightedPoiLayer.current,
                gpsPositionLayer.current,
                skiSlopeIconLayer.current,
            ],
            view: new View({
                center: fromLonLat([
                    parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || '20.2630'),
                    parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || '63.8258')
                ]),
                zoom: parseInt(process.env.REACT_APP_MAP_DEFAULT_ZOOM || '14', 10),
                minZoom: 6,
                maxZoom: 20,
            }),
            ...(process.env.REACT_APP_STANDALONE !== 'true' && {controls: []}),
        });

        // Register OL Map events
        map.current.on('singleclick', (event) => handleSingleClick(event));
        map.current.on('pointerdrag', (event) => handlePointerDrag(event));
        map.current.on('moveend', (event) => handleMoveEnd(event));

        // Expose for quick debugging in the browser console during development
        try {
            (window as any).__olMap = map.current;
        } catch (e) {
            // noop in non-browser environments
        }

        // Signal to React Native that the map is ready to receive commands
        postMessage(JSON.stringify({ mapReady: true }));
    }, []);

    /**
     * Updates map sources based on map type
     */
    const updateMapSources = (type?: MapType, force?: boolean) => {
        switch (type) {
            case MapType.Map:
                // Sync position from 3D if coming from ThreeD
                if (mapType.current === MapType.ThreeD && map3d.current) {
                    const center3d = map3d.current.getCenter();
                    const zoom3d = map3d.current.getZoom();
                    map.current?.getView().setCenter(fromLonLat([center3d.lng, center3d.lat]));
                    map.current?.getView().setZoom(zoom3d);
                }
                destroy3DMap();
                sateliteLayer.current?.setVisible(false);
                hdMapLayer.current?.setVisible(true);
                if (mapRef.current) mapRef.current.style.display = 'flex';
                if (map3dRef.current) map3dRef.current.style.display = 'none';
                break;

            case MapType.Satelite:
                // Sync position from 3D if coming from ThreeD
                if (mapType.current === MapType.ThreeD && map3d.current) {
                    const center3d = map3d.current.getCenter();
                    const zoom3d = map3d.current.getZoom();
                    map.current?.getView().setCenter(fromLonLat([center3d.lng, center3d.lat]));
                    map.current?.getView().setZoom(zoom3d);
                }
                destroy3DMap();
                hdMapLayer.current?.setVisible(false);
                sateliteLayer.current?.setVisible(true);
                if (mapRef.current) mapRef.current.style.display = 'flex';
                if (map3dRef.current) map3dRef.current.style.display = 'none';
                break;

            case MapType.ThreeD:
                if (mapRef.current) mapRef.current.style.display = 'none';
                if (map3dRef.current) map3dRef.current.style.display = 'flex';
                init3DMap();
                break;

            default:
                break;
        }
    };

    /**
     * Handles single click events within the map
     */
     const handleSingleClick = (event: MapBrowserEvent<any>) => {
        let hitFeature:  undefined | RenderFeature | Feature<Geometry>;

        // Itterate each point and see if we hit any feature
        map.current?.forEachFeatureAtPixel(
            event.pixel,
            (rootFeature) => {
                const features = [rootFeature, ...(rootFeature.get('features') ?? [])];
                features.forEach((feature) => {
                    const type = feature.get(OBJECT_TYPE);
                    const id = feature.get(OBJECT_ID);
    
                    const isSelectable = type && id;
                    const hasPriority = !hitFeature || type === FeatureType.Poi;
    
                    if (isSelectable && hasPriority) {
                        hitFeature = feature;
                    }
                });
            },
            {
                hitTolerance: 11 * getRenderScale(),
            },
        );

        // Check if we have data within the detected feature, early return if not
        if (!hitFeature) {
            const msg = JSON.stringify({deselect: true});
            postMessage(msg);
            onMessage?.(msg);
            return;
        }

        const type = hitFeature?.get(OBJECT_TYPE);
        const id = hitFeature?.get(OBJECT_ID);

        const msg = JSON.stringify({type, id});
        postMessage(msg);
        onMessage?.(msg);
    };

    /**
     * Handles drag events within the map
     */
    const handlePointerDrag = (event: MapBrowserEvent<any>) => {
        if (gpsIsCentered.current) {
            const msg = JSON.stringify({centered: false});
            postMessage(msg);
            onMessage?.(msg);

            gpsIsCentered.current = false;
        }
    };

    /**
     * Handles move end events within the map
     */
    const handleMoveEnd = (event: MapEvent) => {
        updateMapSources(mapType.current);
    };

    /**
     * Sets up a map of the properties associated with each PoI category
     */
    const setPoiCategoryProps = (poiCategories: MapCategories) => {
        poiCategoryProps.current = {};
        for(const [key, value] of Object.entries(poiCategories)) {
            // Save Identifier data for this poi id
            const props = (PROPS.POIS as any)[value.identifier];
            if (!props) {
                console.warn(`Unknown POI category: ${value.identifier}, using default fallback`);
                poiCategoryProps.current[key] = {
                    color: COLORS.POIS.DEFAULT || '#666666',
                    icon: 'openlayers/icons/pois/poi-default.png',
                    closedIcon: 'openlayers/icons/pois/poi-default.png'
                };
            } else {
                poiCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Sets up a map of the properties associated with each Path category
     */
    const setPathCategoryProps = (pathCategories: MapCategories) => {
        pathCategoryProps.current = {};
        for(const [key, value] of Object.entries(pathCategories)) {
            // Save Identifier data for this path id
            const props = (PROPS.PATHS as any)[value.identifier];
            if (!props) {
                console.warn(`Unknown Path category: ${value.identifier}, using default fallback`);
                pathCategoryProps.current[key] = {
                    color: COLORS.PATHS.DEFAULT || '#666666',
                    icon: '',
                    closedIcon: ''
                };
            } else {
                pathCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Sets up a map of the properties associated with each Path category
     */
    const setAoiCategoryProps = (aoiCategories: MapCategories) => {
        aoiCategoryProps.current = {};
        for(const [key, value] of Object.entries(aoiCategories)) {
            // Save Identifier data for this aoi id
            const props = (PROPS.AOIS as any)[value.identifier];
            if (!props) {
                console.warn(`Unknown AOI category: ${value.identifier}, using default fallback`);
                aoiCategoryProps.current[key] = {
                    color: COLORS.AOIS.DEFAULT || '#666666',
                    icon: '',
                    closedIcon: ''
                };
            } else {
                aoiCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Redraws all the map sources given the current filters, selection, etc.
     */
    const redraw = () => {
        aoiSource.current?.clear();
        pathSource.current?.clear();
        snowmobilePathSource.current?.clear();
        poiSource.current?.clear();
        snowmobileIconSource.current?.clear();
        hikingIconSource.current?.clear();
        crossCountrySkiingIconSource.current?.clear();
        mountainBikingIconSource.current?.clear();
        runningIconSource.current?.clear();
        ridingIconSource.current?.clear();
        canoeIconSource.current?.clear();
        chairLiftIconSource.current?.clear();
        gondolaIconSource.current?.clear();
        surfaceLiftIconSource.current?.clear();
        beltLiftIconSource.current?.clear();
        skiSlopeIconSource.current?.clear();
        
        if (!filterData.current?.length) {
            drawPaths([]);
            drawSnowmobilePaths([]);
            drawPois([]);
            drawAois([]);
        } else {
            filterData.current.forEach((mapFilter: FeatureGroup) => {
                switch (mapFilter.type) {
                    case FeatureType.Path:
                        drawPaths(mapFilter.ids);
                        break;
            
                    case FeatureType.SnowmobilePath:
                        drawSnowmobilePaths(mapFilter.ids);
                        break;

                    case FeatureType.SnowmobileSubPath:
                        drawSnowmobileSubPaths(mapFilter.ids);
                        break;
            
                    case FeatureType.Poi:
                        drawPois(mapFilter.ids);
                        break;
                    
                    case FeatureType.Aoi:
                        drawAois(mapFilter.ids);
                        break;
            
                    default:
                        break;
                }
            });
        }

        if (selectedType.current !== undefined && selectedId.current !== undefined) {
            redrawHighlight();
        }

        // Sync data to 3D map if active
        if (mapType.current === MapType.ThreeD) {
            sync3DData();
        }
    };

    // ── 3D Map (Mapbox GL JS) ──────────────────────────────────────────────

    /**
     * Builds a GeoJSON FeatureCollection of visible paths (incl. snowmobile)
     */
    const buildPathsGeoJSON = (): any => {
        const features: any[] = [];
        if (!pathData.current || !pathGpsData.current) {
            console.log('[3D] buildPathsGeoJSON: no pathData or pathGpsData', {
                hasPathData: !!pathData.current,
                pathDataKeys: pathData.current ? Object.keys(pathData.current).length : 0,
                hasGpsData: !!pathGpsData.current,
                gpsDataKeys: pathGpsData.current ? Object.keys(pathGpsData.current).length : 0,
            });
            return { type: 'FeatureCollection', features };
        }

        // When no filters are active, show ALL paths (same as 2D redraw)
        const noFilters = !filterData.current?.length;

        // Determine filter
        let pathIds: string[] | null = null;
        let snowSubIds: string[] | null = null;
        let hasPathFilter = noFilters; // true when no filters = show all
        let hasSnowFilter = noFilters;

        if (!noFilters) {
            const pf = filterData.current!.find(f => f.type === FeatureType.Path);
            hasPathFilter = !!pf;
            pathIds = pf?.ids ?? null;

            const sf = filterData.current!.find(f =>
                f.type === FeatureType.SnowmobilePath || f.type === FeatureType.SnowmobileSubPath
            );
            hasSnowFilter = !!sf;
            if (sf?.type === FeatureType.SnowmobilePath) {
                snowSubIds = [];
                sf.ids.forEach(id => {
                    const sp = snowmobilePathData.current[id];
                    sp?.subPaths?.forEach(sub => snowSubIds!.push(typeof sub === 'string' ? sub : sub.id));
                });
            } else if (sf) {
                snowSubIds = sf.ids;
            }
        }

        // Regular paths
        if (hasPathFilter) {
            Object.values(pathData.current).forEach(path => {
                if (pathIds && pathIds.length && !pathIds.includes(path.id)) return;
                const coords = pathGpsData.current[path.id];
                if (!coords || coords.length === 0) return;

                const color = path.pathCategoryId === PathCategoryId.SkiSlope
                    ? ((PROPS.PATHS as any).DIFFICULTY?.[path.difficulty] || COLORS.PATHS.CROSS_COUNTRY_SKIING)
                    : (pathCategoryProps.current?.[path.pathCategoryId]?.color || COLORS.PATHS.CROSS_COUNTRY_SKIING);

                features.push({
                    type: 'Feature',
                    properties: {
                        id: path.id,
                        featureType: FeatureType.Path,
                        color,
                        opacity: path.status === PathStatus.Closed ? 0.5 : 1,
                    },
                    geometry: {
                        type: 'LineString',
                        coordinates: coords.map((c: any) => [c.longitude, c.latitude]),
                    }
                });
            });
        }

        // Snowmobile sub-paths
        if (hasSnowFilter && snowmobileSubPathData.current) {
            Object.values(snowmobileSubPathData.current).forEach(sub => {
                if (snowSubIds && snowSubIds.length && !snowSubIds.includes(sub.id)) return;
                const coords = sub.subPathGPSCoordinates;
                if (!coords || coords.length === 0) return;

                features.push({
                    type: 'Feature',
                    properties: {
                        id: sub.id,
                        featureType: FeatureType.SnowmobileSubPath,
                        color: COLORS.PATHS.SNOWMOBILING,
                        opacity: sub.status === PathStatus.Closed ? 0.5 : 1,
                    },
                    geometry: {
                        type: 'LineString',
                        coordinates: coords.map((c: any) => [c.longitude, c.latitude]),
                    }
                });
            });
        }

        return { type: 'FeatureCollection', features };
    };

    /**
     * Builds a GeoJSON FeatureCollection of visible POIs
     */
    const buildPoisGeoJSON = (): any => {
        const features: any[] = [];
        if (!poiData.current) return { type: 'FeatureCollection', features };

        const noFilters = !filterData.current?.length;
        let poiIds: string[] | null = null;
        let hasFilter = noFilters; // true when no filters = show all
        if (!noFilters) {
            const pf = filterData.current!.find(f => f.type === FeatureType.Poi);
            hasFilter = !!pf;
            poiIds = pf?.ids ?? null;
        }
        if (!hasFilter) return { type: 'FeatureCollection', features };

        Object.values(poiData.current).forEach(poi => {
            if (poiIds && poiIds.length && !poiIds.includes(poi.id)) return;
            const color = poiCategoryProps.current?.[poi.poiCategoryId]?.color || '#666666';
            features.push({
                type: 'Feature',
                properties: {
                    id: poi.id,
                    featureType: FeatureType.Poi,
                    name: getTranslatedProperty(selectedLanguage.current, 'name', poi),
                    color,
                    iconImage: `poi-cat-${poi.poiCategoryId}`,
                },
                geometry: {
                    type: 'Point',
                    coordinates: [poi.longitude, poi.latitude],
                }
            });
        });
        return { type: 'FeatureCollection', features };
    };

    /**
     * Builds a GeoJSON FeatureCollection of visible AOIs
     */
    const buildAoisGeoJSON = (): any => {
        const features: any[] = [];
        if (!aoiData.current || !aoiGpsData.current) return { type: 'FeatureCollection', features };

        const noFilters = !filterData.current?.length;
        let aoiIds: string[] | null = null;
        let hasFilter = noFilters; // true when no filters = show all
        if (!noFilters) {
            const af = filterData.current!.find(f => f.type === FeatureType.Aoi);
            hasFilter = !!af;
            aoiIds = af?.ids ?? null;
        }
        if (!hasFilter) return { type: 'FeatureCollection', features };

        Object.values(aoiData.current).forEach(aoi => {
            if (aoiIds && aoiIds.length && !aoiIds.includes(aoi.id)) return;
            const coords = aoiGpsData.current[aoi.id];
            if (!coords || coords.length === 0) return;
            const color = aoiCategoryProps.current?.[aoi.aoiCategoryId]?.color || '#666666';
            const ring = coords.map((c: any) => [c.longitude, c.latitude]);
            // Close ring
            if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                ring.push([...ring[0]]);
            }
            features.push({
                type: 'Feature',
                properties: {
                    id: aoi.id,
                    featureType: FeatureType.Aoi,
                    color,
                    name: getTranslatedProperty(selectedLanguage.current, 'name', aoi),
                },
                geometry: {
                    type: 'Polygon',
                    coordinates: [ring],
                }
            });
        });
        return { type: 'FeatureCollection', features };
    };

    /**
     * Destroys the 3D Mapbox GL map instance completely
     */
    const destroy3DMap = () => {
        console.log('[3D] destroy3DMap called v2');
        if (map3d.current) {
            try { map3d.current.remove(); } catch (e) { /* ignore */ }
            map3d.current = null;
            map3dLoaded.current = false;
        }
    };

    /**
     * Generates a combined backplate + icon canvas for a path category icon.
     * Matches the 2D map's rounded-rect style (white fill, colored border, tinted icon).
     */
    const create3DPathIconImage = (iconImg: HTMLImageElement, color: string, size: number = 48): ImageData => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        const borderWidth = Math.max(2, size * 0.06);
        const radius = size * 0.30;
        const inset = borderWidth / 2;

        // Draw rounded rect backplate
        ctx.beginPath();
        ctx.moveTo(inset + radius, inset);
        ctx.lineTo(size - inset - radius, inset);
        ctx.arcTo(size - inset, inset, size - inset, inset + radius, radius);
        ctx.lineTo(size - inset, size - inset - radius);
        ctx.arcTo(size - inset, size - inset, size - inset - radius, size - inset, radius);
        ctx.lineTo(inset + radius, size - inset);
        ctx.arcTo(inset, size - inset, inset, size - inset - radius, radius);
        ctx.lineTo(inset, inset + radius);
        ctx.arcTo(inset, inset, inset + radius, inset, radius);
        ctx.closePath();

        // White fill
        ctx.fillStyle = 'white';
        ctx.fill();

        // Colored border
        ctx.strokeStyle = color;
        ctx.lineWidth = borderWidth;
        ctx.stroke();

        // Draw the icon centered, scaled to fit inside backplate
        const iconPad = size * 0.2;
        const iconArea = size - iconPad * 2;
        const aspect = iconImg.width / iconImg.height;
        let drawW, drawH;
        if (aspect > 1) {
            drawW = iconArea;
            drawH = iconArea / aspect;
        } else {
            drawH = iconArea;
            drawW = iconArea * aspect;
        }
        const drawX = (size - drawW) / 2;
        const drawY = (size - drawH) / 2;

        // Tint the icon with the path color
        // First draw icon to offscreen canvas, then composite with color
        const tintCanvas = document.createElement('canvas');
        tintCanvas.width = size;
        tintCanvas.height = size;
        const tCtx = tintCanvas.getContext('2d')!;
        tCtx.drawImage(iconImg, drawX, drawY, drawW, drawH);
        tCtx.globalCompositeOperation = 'source-in';
        tCtx.fillStyle = color;
        tCtx.fillRect(0, 0, size, size);

        ctx.drawImage(tintCanvas, 0, 0);

        return ctx.getImageData(0, 0, size, size);
    };

    /**
     * Generates a combined white-circle + tinted-icon canvas for a POI category icon.
     * Matches the 2D map's circle backplate style.
     */
    const create3DPoiIconImage = (iconImg: HTMLImageElement, color: string, size: number = 48): ImageData => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        // White circle backplate (matches 2D map's backplate style)
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.fill();

        // Draw the icon centered — icon fills ~85% of circle (matching 2D ratio)
        const iconPad = size * 0.10;
        const iconArea = size - iconPad * 2;
        const aspect = iconImg.width / iconImg.height;
        let drawW, drawH;
        if (aspect > 1) {
            drawW = iconArea;
            drawH = iconArea / aspect;
        } else {
            drawH = iconArea;
            drawW = iconArea * aspect;
        }
        const drawX = (size - drawW) / 2;
        const drawY = (size - drawH) / 2;

        // Tint the icon with the category color
        const tintCanvas = document.createElement('canvas');
        tintCanvas.width = size;
        tintCanvas.height = size;
        const tCtx = tintCanvas.getContext('2d')!;
        tCtx.drawImage(iconImg, drawX, drawY, drawW, drawH);
        tCtx.globalCompositeOperation = 'source-in';
        tCtx.fillStyle = color;
        tCtx.fillRect(0, 0, size, size);

        ctx.drawImage(tintCanvas, 0, 0);

        return ctx.getImageData(0, 0, size, size);
    };

    /**
     * Loads all POI category icons into a Mapbox GL map as images.
     * Returns a promise that resolves when all icons are loaded.
     */
    const load3DPoiIcons = (m: mapboxgl.Map): Promise<void> => {
        const iconDefs: { [key: string]: { url: string, color: string } } = {};

        // Build from poiCategoryProps (same mapping as 2D)
        if (poiCategoryProps.current) {
            for (const [catId, props] of Object.entries(poiCategoryProps.current)) {
                const { icon, color } = props as { icon: string; color: string; closedIcon: string };
                if (icon) {
                    iconDefs[`poi-cat-${catId}`] = { url: icon, color };
                }
            }
        }

        const loadPromises = Object.entries(iconDefs).map(([imgId, { url, color }]) => {
            return new Promise<void>((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    try {
                        if (!m.hasImage(imgId)) {
                            const imageData = create3DPoiIconImage(img, color, 64);
                            m.addImage(imgId, imageData, { pixelRatio: 2 });
                        }
                    } catch (e) {
                        console.warn('[3D] Failed to add POI icon image:', imgId, e);
                    }
                    resolve();
                };
                img.onerror = () => {
                    console.warn('[3D] Failed to load POI icon:', url);
                    resolve();
                };
                img.src = url;
            });
        });

        return Promise.all(loadPromises).then(() => {
            console.log('[3D] All POI icons loaded:', Object.keys(iconDefs).length);
        });
    };

    /**
     * Loads all path category icons into a Mapbox GL map as images.
     * Returns a promise that resolves when all icons are loaded.
     */
    const load3DPathIcons = (m: mapboxgl.Map): Promise<void> => {
        // Map of icon image ID -> { url, color }
        const iconDefs: { [key: string]: { url: string, color: string } } = {};

        // Build from PROPS (same mapping as 2D map)
        if (pathCategoryProps.current) {
            for (const [catId, props] of Object.entries(pathCategoryProps.current)) {
                const { icon, color } = props as { icon: string; color: string; closedIcon: string };
                if (icon) {
                    iconDefs[`path-cat-${catId}`] = { url: icon, color };
                }
            }
        }

        // Fallback: define for all known categories from ICONS constants
        const fallbackDefs: { [key: string]: { url: string, color: string } } = {
            [`path-cat-${PathCategoryId.CrossCountrySkiing}`]: { url: ICONS.PATHS.CROSS_COUNTRY_SKIING, color: COLORS.PATHS.CROSS_COUNTRY_SKIING },
            [`path-cat-${PathCategoryId.Snowmobiling}`]:      { url: ICONS.PATHS.SNOWMOBILING, color: COLORS.PATHS.SNOWMOBILING },
            [`path-cat-${PathCategoryId.MountainBiking}`]:    { url: ICONS.PATHS.MOUNTAIN_BIKING, color: COLORS.PATHS.MOUNTAIN_BIKING },
            [`path-cat-${PathCategoryId.Hiking}`]:            { url: ICONS.PATHS.HIKING, color: COLORS.PATHS.HIKING },
            [`path-cat-${PathCategoryId.Running}`]:           { url: ICONS.PATHS.RUNNING, color: COLORS.PATHS.RUNNING },
            [`path-cat-${PathCategoryId.Riding}`]:            { url: ICONS.PATHS.RIDING, color: COLORS.PATHS.RIDING },
            [`path-cat-${PathCategoryId.Canoe}`]:             { url: ICONS.PATHS.CANOE, color: COLORS.PATHS.CANOE },
            [`path-cat-${PathCategoryId.ChairLift}`]:         { url: ICONS.PATHS.CHAIR_LIFT, color: COLORS.PATHS.CHAIR_LIFT },
            [`path-cat-${PathCategoryId.Gondola}`]:           { url: ICONS.PATHS.GONDOLA, color: COLORS.PATHS.GONDOLA },
            [`path-cat-${PathCategoryId.SurfaceLift}`]:       { url: ICONS.PATHS.SURFACE_LIFT, color: COLORS.PATHS.SURFACE_LIFT },
            [`path-cat-${PathCategoryId.BeltLift}`]:          { url: ICONS.PATHS.BELT_LIFT, color: COLORS.PATHS.BELT_LIFT },
            [`path-cat-${PathCategoryId.SkiSlope}`]:          { url: ICONS.PATHS.SKI_SLOPE, color: COLORS.PATHS.SKI_SLOPE },
        };

        // Merge fallbacks for any missing categories
        for (const [key, def] of Object.entries(fallbackDefs)) {
            if (!iconDefs[key]) iconDefs[key] = def;
        }

        const loadPromises = Object.entries(iconDefs).map(([imgId, { url, color }]) => {
            return new Promise<void>((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    try {
                        if (!m.hasImage(imgId)) {
                            const imageData = create3DPathIconImage(img, color, 64);
                            m.addImage(imgId, imageData, { pixelRatio: 2 });
                        }
                    } catch (e) {
                        console.warn('[3D] Failed to add icon image:', imgId, e);
                    }
                    resolve();
                };
                img.onerror = () => {
                    console.warn('[3D] Failed to load icon:', url);
                    resolve(); // Don't block on failure
                };
                img.src = url;
            });
        });

        return Promise.all(loadPromises).then(() => {
            console.log('[3D] All path icons loaded:', Object.keys(iconDefs).length);
        });
    };

    /**
     * Initializes the Mapbox GL 3D map with terrain (always creates fresh)
     */
    const init3DMap = () => {
        if (!map3dRef.current) return;

        // Get current OL view center and zoom to sync
        const view = map.current?.getView();
        const olCenter = view ? toLonLat(view.getCenter()!) : [
            parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || '20.2630'),
            parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || '63.8258')
        ];
        const olZoom = view?.getZoom() || parseInt(process.env.REACT_APP_MAP_DEFAULT_ZOOM || '14', 10);

        // Destroy previous instance if it exists
        destroy3DMap();

        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

        map3d.current = new mapboxgl.Map({
            container: map3dRef.current,
            style: `mapbox://styles/${MAPBOX_STYLE_USER}/${MAPBOX_STYLE_ID}`,
            center: [olCenter[0], olCenter[1]],
            zoom: olZoom,
            pitch: 60,
            bearing: -17,
            antialias: true,
            maxZoom: 20,
            minPitch: 0,
            maxPitch: 85,
        });

        map3d.current.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'bottom-left');

        // Tune drag interaction for smoother 3D terrain navigation
        // Reduce inertia so the map doesn't "jump" when releasing a drag
        if (map3d.current.dragPan) {
            map3d.current.dragPan.enable({
                linearity: 0.15,
                maxSpeed: 1000,
                deceleration: 3000,
            } as any);
        }

        // Tune touch zoom/rotate for 3D terrain navigation
        if (map3d.current.touchZoomRotate) {
            // Rotation enabled for 3D navigation
        }

        // Tune touch pitch for smoother tilt gestures
        if (map3d.current.touchPitch) {
            map3d.current.touchPitch.enable({
                around: 'center',
            } as any);
        }

        map3d.current.on('load', () => {
          try {
            const m = map3d.current!;
            map3dLoaded.current = true;
            console.log('[3D] Mapbox GL map loaded, adding terrain + syncing data');
            console.log('[3D] filterData:', JSON.stringify(filterData.current));
            console.log('[3D] pathData keys:', pathData.current ? Object.keys(pathData.current).length : 0);
            console.log('[3D] pathGpsData keys:', pathGpsData.current ? Object.keys(pathGpsData.current).length : 0);

            // Helper: safely add source/layer only if not already present
            const safeAddSource = (id: string, spec: any) => {
                if (!m.getSource(id)) m.addSource(id, spec);
            };
            const safeAddLayer = (spec: any) => {
                if (!m.getLayer(spec.id)) m.addLayer(spec);
            };

            // Add terrain DEM source
            safeAddSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: 14,
            });
            m.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });

            // Add sky atmosphere
            safeAddLayer({
                id: 'sky',
                type: 'sky',
                paint: {
                    'sky-type': 'atmosphere',
                    'sky-atmosphere-sun': [0.0, 90.0],
                    'sky-atmosphere-sun-intensity': 15,
                },
            });

            // Pre-create sources + layers WITH data so rendering starts immediately
            const initPathGeoJSON = buildPathsGeoJSON();
            const initAoiGeoJSON = buildAoisGeoJSON();
            const initPoiGeoJSON = buildPoisGeoJSON();
            console.log('[3D] init data: paths=' + initPathGeoJSON.features.length +
                ', aois=' + initAoiGeoJSON.features.length +
                ', pois=' + initPoiGeoJSON.features.length);

            // Paths — white outline + colored line on top (same as 2D map)
            safeAddSource('paths-source', { type: 'geojson', data: initPathGeoJSON });
            // White outline (4 inner + 6 border = 10 total)
            safeAddLayer({
                id: 'paths-outline-layer',
                type: 'line',
                source: 'paths-source',
                paint: {
                    'line-color': '#FFFFFF',
                    'line-width': 10,
                    'line-opacity': ['coalesce', ['get', 'opacity'], 1],
                },
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                }
            });
            // Colored path line on top
            safeAddLayer({
                id: 'paths-layer',
                type: 'line',
                source: 'paths-source',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 4,
                    'line-opacity': ['coalesce', ['get', 'opacity'], 1],
                },
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                }
            });

            // Build path icon midpoints from initial path data
            const initPathIcons: any[] = [];
            initPathGeoJSON.features.forEach((f: any) => {
                if (f.geometry?.type === 'LineString' && f.geometry.coordinates?.length > 1) {
                    const coords = f.geometry.coordinates;
                    const midCoord = coords[Math.floor(coords.length / 2)];
                    const pathObj = pathData.current?.[f.properties.id];
                    const catId = pathObj?.pathCategoryId || PathCategoryId.Hiking;
                    initPathIcons.push({
                        type: 'Feature',
                        properties: {
                            id: f.properties.id,
                            featureType: f.properties.featureType,
                            color: f.properties.color,
                            iconImage: `path-cat-${catId}`,
                        },
                        geometry: { type: 'Point', coordinates: midCoord },
                    });
                }
            });

            // Path icon markers (midpoints) — load icons then add layers
            safeAddSource('path-icons-source', { type: 'geojson', data: { type: 'FeatureCollection', features: initPathIcons } });

            // Load actual PNG icons (same as 2D map) then add symbol layer
            load3DPathIcons(m).then(() => {
                safeAddLayer({
                    id: 'path-icons-layer',
                    type: 'symbol',
                    source: 'path-icons-source',
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 0.85,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                    },
                    minzoom: 13,
                });
                m.triggerRepaint();
            });

            // AOIs
            safeAddSource('aois-source', { type: 'geojson', data: initAoiGeoJSON });
            safeAddLayer({
                id: 'aois-fill-layer',
                type: 'fill',
                source: 'aois-source',
                paint: {
                    'fill-color': ['get', 'color'],
                    'fill-opacity': 0.3,
                }
            });
            safeAddLayer({
                id: 'aois-outline-layer',
                type: 'line',
                source: 'aois-source',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 2,
                }
            });

            // POIs — load icons then add symbol layer (same pattern as path icons)
            safeAddSource('pois-source', { type: 'geojson', data: initPoiGeoJSON });

            load3DPoiIcons(m).then(() => {
                safeAddLayer({
                    id: 'pois-layer',
                    type: 'symbol',
                    source: 'pois-source',
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 0.85,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                        'text-field': ['get', 'name'],
                        'text-size': 12,
                        'text-offset': [0, 1.8],
                        'text-anchor': 'top',
                        'text-optional': true,
                    },
                    paint: {
                        'text-color': '#333',
                        'text-halo-color': 'white',
                        'text-halo-width': 1,
                    },
                    minzoom: 13,
                });
                m.triggerRepaint();
            });

            // GPS — load same icon as 2D map (gps-position.png) with heading rotation
            safeAddSource('gps-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

            // Load GPS icon via HTML Image (works with relative paths in WebView)
            const gpsImg = new Image();
            gpsImg.crossOrigin = 'anonymous';
            gpsImg.onload = () => {
                try {
                    if (!m.hasImage('gps-icon')) {
                        const canvas = document.createElement('canvas');
                        canvas.width = gpsImg.width;
                        canvas.height = gpsImg.height;
                        const ctx = canvas.getContext('2d')!;
                        ctx.drawImage(gpsImg, 0, 0);
                        const imageData = ctx.getImageData(0, 0, gpsImg.width, gpsImg.height);
                        m.addImage('gps-icon', imageData, { pixelRatio: 2 });
                    }
                    safeAddLayer({
                        id: 'gps-layer',
                        type: 'symbol',
                        source: 'gps-source',
                        layout: {
                            'icon-image': 'gps-icon',
                            'icon-size': 1.1,
                            'icon-rotate': ['get', 'heading'],
                            'icon-rotation-alignment': 'map',
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                        },
                    });
                    m.triggerRepaint();
                } catch (e) {
                    console.warn('[3D] Failed to add GPS icon:', e);
                }
            };
            gpsImg.onerror = () => {
                console.warn('[3D] Failed to load GPS icon from:', ICONS.GPS_POSITION);
            };
            gpsImg.src = ICONS.GPS_POSITION;

            // Delayed re-syncs to ensure rendering after terrain settles
            setTimeout(() => { sync3DData(); m.triggerRepaint(); }, 500);
            setTimeout(() => { sync3DData(); m.triggerRepaint(); }, 2000);

            // Unified click handler with bbox tolerance for easier line hits
            m.on('click', (e: any) => {
                const tolerance = 10;
                const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
                    [e.point.x - tolerance, e.point.y - tolerance],
                    [e.point.x + tolerance, e.point.y + tolerance]
                ];
                const layers = ['paths-layer', 'paths-outline-layer', 'path-icons-layer', 'pois-layer', 'aois-fill-layer'];
                const features = m.queryRenderedFeatures(bbox, { layers });
                console.log('[3D] click at', e.point, 'features found:', features.length);

                if (features.length > 0) {
                    const f = features[0];
                    const props = f.properties || {};
                    const id = props.id;
                    const featureType = props.featureType;
                    console.log('[3D] clicked feature:', { id, featureType, layer: f.layer?.id });
                    if (id && featureType) {
                        const msg = JSON.stringify({ type: featureType, id: String(id) });
                        postMessage(msg);
                        onMessage?.(msg);
                    }
                } else {
                    const msg = JSON.stringify({ deselect: true });
                    postMessage(msg);
                    onMessage?.(msg);
                }
            });

            // Drag invalidates GPS centered
            m.on('dragstart', () => {
                if (gpsIsCentered.current) {
                    const msg = JSON.stringify({ centered: false });
                    postMessage(msg);
                    onMessage?.(msg);
                    gpsIsCentered.current = false;
                }
            });

            // Cursor hints
            ['paths-layer', 'paths-outline-layer', 'path-icons-layer', 'pois-layer', 'aois-fill-layer'].forEach(layer => {
                m.on('mouseenter', layer, () => { m.getCanvas().style.cursor = 'pointer'; });
                m.on('mouseleave', layer, () => { m.getCanvas().style.cursor = ''; });
            });

          } catch (err: any) {
            console.error('[3D] load handler error:', err);
            // Show error visually since we can't access WebView console
            const errDiv = document.createElement('div');
            errDiv.style.cssText = 'position:fixed;top:60px;left:10px;right:10px;background:red;color:white;padding:12px;border-radius:6px;font:13px monospace;z-index:99999;word-wrap:break-word;';
            errDiv.textContent = `3D ERROR: ${err?.message || err}`;
            document.body.appendChild(errDiv);
          }
        });

        // Catch Mapbox GL errors (style load failures, etc.)
        map3d.current.on('error', (e: any) => {
            console.error('[3D] Mapbox error:', e?.error?.message || e);
        });

        // Re-sync data once terrain + all tiles are fully rendered
        map3d.current.once('idle', () => {
            console.log('[3D] map idle — re-syncing data');
            sync3DData();
        });
    };

    /**
     * Syncs current path/POI/AOI data to the 3D Mapbox GL map.
     * Sources + layers are pre-created in init3DMap; we only setData here.
     */
    const sync3DData = () => {
        const m = map3d.current;
        if (!m || !map3dLoaded.current) {
            console.log('[3D] sync3DData: skipped', { hasMap: !!m, loaded: map3dLoaded.current });
            return;
        }

        // --- Paths ---
        const pathGeoJSON = buildPathsGeoJSON();
        console.log('[3D] sync3DData: paths features:', pathGeoJSON.features.length);
        const pathSrc = m.getSource('paths-source') as mapboxgl.GeoJSONSource;
        if (pathSrc) pathSrc.setData(pathGeoJSON);

        // --- Path icon markers at midpoints ---
        const pathIconFeatures: any[] = [];
        pathGeoJSON.features.forEach((f: any) => {
            if (f.geometry?.type === 'LineString' && f.geometry.coordinates?.length > 1) {
                const coords = f.geometry.coordinates;
                const midIdx = Math.floor(coords.length / 2);
                const midCoord = coords[midIdx];

                // Use same icon as 2D map based on path category
                const pathObj = pathData.current?.[f.properties.id];
                const catId = pathObj?.pathCategoryId || PathCategoryId.Hiking;

                pathIconFeatures.push({
                    type: 'Feature',
                    properties: {
                        id: f.properties.id,
                        featureType: f.properties.featureType,
                        color: f.properties.color,
                        name: pathObj ? getTranslatedProperty(selectedLanguage.current, 'name', pathObj) : '',
                        iconImage: `path-cat-${catId}`,
                    },
                    geometry: { type: 'Point', coordinates: midCoord },
                });
            }
        });
        const pathIconSrc = m.getSource('path-icons-source') as mapboxgl.GeoJSONSource;
        if (pathIconSrc) pathIconSrc.setData({ type: 'FeatureCollection', features: pathIconFeatures });

        // --- AOIs ---
        const aoiGeoJSON = buildAoisGeoJSON();
        console.log('[3D] sync3DData: aoi features:', aoiGeoJSON.features.length);
        const aoiSrc = m.getSource('aois-source') as mapboxgl.GeoJSONSource;
        if (aoiSrc) aoiSrc.setData(aoiGeoJSON);

        // --- POIs ---
        const poiGeoJSON = buildPoisGeoJSON();
        console.log('[3D] sync3DData: poi features:', poiGeoJSON.features.length);
        const poiSrc = m.getSource('pois-source') as mapboxgl.GeoJSONSource;
        if (poiSrc) poiSrc.setData(poiGeoJSON);

        // --- GPS position ---
        const gpsSrc = m.getSource('gps-source') as mapboxgl.GeoJSONSource;
        if (gpsSrc) {
            if (gpsPositionData.current) {
                const { longitude, latitude } = gpsPositionData.current;
                const headingDeg = headingData.current != null ? (headingData.current * 180 / Math.PI) : 0;
                gpsSrc.setData({
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        properties: { heading: headingDeg },
                        geometry: { type: 'Point', coordinates: [longitude, latitude] }
                    }]
                });
            } else {
                gpsSrc.setData({ type: 'FeatureCollection', features: [] });
            }
        }
    };

    /**
     * Draws all the paths provided in pathData, filter with optional ID array
     */
    const drawPaths = (ids?: string[]) => {
        if (!pathData.current || !Object.keys(pathData.current).length) {
            return;
        }

        const crossCountrySkiingLineFeatures: Feature<Geometry>[] = [];
        const crossCountrySkiingIconFeatures: Feature<Geometry>[] = [];
        const crossCountrySkiingEndcapFeatures: Feature<Geometry>[] = [];
      
        const mountainBikingLineFeatures: Feature<Geometry>[] = [];
        const mountainBikingIconFeatures: Feature<Geometry>[] = [];
        const mountainBikingEndcapFeatures: Feature<Geometry>[] = [];
      
        const hikingLineFeatures: Feature<Geometry>[] = [];
        const hikingIconFeatures: Feature<Geometry>[] = [];
        const hikingEndcapFeatures: Feature<Geometry>[] = [];

        const runningLineFeatures: Feature<Geometry>[] = [];
        const runningIconFeatures: Feature<Geometry>[] = [];
        const runningEndcapFeatures: Feature<Geometry>[] = [];

        const ridingLineFeatures: Feature<Geometry>[] = [];
        const ridingIconFeatures: Feature<Geometry>[] = [];
        const ridingEndcapFeatures: Feature<Geometry>[] = [];

        const canoeLineFeatures: Feature<Geometry>[] = [];
        const canoeIconFeatures: Feature<Geometry>[] = [];
        const canoeEndcapFeatures: Feature<Geometry>[] = [];

        const chairLiftLineFeatures: Feature<Geometry>[] = [];
        const chairLiftIconFeatures: Feature<Geometry>[] = [];
        const chairLiftEndcapFeatures: Feature<Geometry>[] = [];

        const gondolaLineFeatures: Feature<Geometry>[] = [];
        const gondolaIconFeatures: Feature<Geometry>[] = [];
        const gondolaEndcapFeatures: Feature<Geometry>[] = [];

        const surfaceLiftLineFeatures: Feature<Geometry>[] = [];
        const surfaceLiftIconFeatures: Feature<Geometry>[] = [];
        const surfaceLiftEndcapFeatures: Feature<Geometry>[] = [];

        const beltLiftLineFeatures: Feature<Geometry>[] = [];
        const beltLiftIconFeatures: Feature<Geometry>[] = [];
        const beltLiftEndcapFeatures: Feature<Geometry>[] = [];

        const skiSlopeLineFeatures: Feature<Geometry>[] = [];
        const skiSlopeIconFeatures: Feature<Geometry>[] = [];
        const skiSlopeEndcapFeatures: Feature<Geometry>[] = [];

        // Sort paths so closed paths are rendered first (underneath open paths)
        const sortedPaths = Object.values(pathData.current).sort((a, b) => {
            const aIsClosed = a.status === PathStatus.Closed ? 0 : 1;
            const bIsClosed = b.status === PathStatus.Closed ? 0 : 1;
            return aIsClosed - bIsClosed;
        });

        sortedPaths.forEach((path) => {
            if (!ids?.length || ids.includes(path.id)) {

                switch(path.pathCategoryId) {
                    case PathCategoryId.CrossCountrySkiing:
                        drawPath(
                            crossCountrySkiingLineFeatures,
                            crossCountrySkiingIconFeatures,
                            crossCountrySkiingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.MountainBiking:
                        drawPath(
                            mountainBikingLineFeatures,
                            mountainBikingIconFeatures,
                            mountainBikingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Hiking:
                        drawPath(
                            hikingLineFeatures,
                            hikingIconFeatures,
                            hikingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Running:
                        drawPath(
                            runningLineFeatures,
                            runningIconFeatures,
                            runningEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Riding:
                        drawPath(
                            ridingLineFeatures,
                            ridingIconFeatures,
                            ridingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Canoe:
                        drawPath(
                            canoeLineFeatures,
                            canoeIconFeatures,
                            canoeEndcapFeatures,
                            path,
                        );
                        break;
                    
                    case PathCategoryId.ChairLift:
                        drawPath(
                            chairLiftLineFeatures,
                            chairLiftIconFeatures,
                            chairLiftEndcapFeatures,
                            path,
                        );
                        break;  

                    case PathCategoryId.Gondola:
                        drawPath(
                            gondolaLineFeatures,
                            gondolaIconFeatures,
                            gondolaEndcapFeatures,
                            path,   
                        );
                        break;
                    
                    case PathCategoryId.SurfaceLift:
                        drawPath(
                            surfaceLiftLineFeatures,
                            surfaceLiftIconFeatures,
                            surfaceLiftEndcapFeatures,
                            path,
                        );
                        break;
                    
                    case PathCategoryId.BeltLift:
                        drawPath(
                            beltLiftLineFeatures,
                            beltLiftIconFeatures,
                            beltLiftEndcapFeatures,
                            path,
                        );
                        break;  

                    case PathCategoryId.SkiSlope:
                        drawPath(
                            skiSlopeLineFeatures,
                            skiSlopeIconFeatures,
                            skiSlopeEndcapFeatures,
                            path,
                        );
                        break;

                    default:
                        break;
                }
            }
        });

        // Add all the features for cross country skiing
        pathSource.current?.addFeatures([
          ...crossCountrySkiingLineFeatures,
          ...crossCountrySkiingEndcapFeatures,
        ]);
        crossCountrySkiingIconSource.current?.addFeatures([...crossCountrySkiingIconFeatures]);
        // Add all the features for mountain biking
        pathSource.current?.addFeatures([
          ...mountainBikingLineFeatures,
          ...mountainBikingEndcapFeatures,
        ]);
        mountainBikingIconSource.current?.addFeatures([...mountainBikingIconFeatures]);
        // Add all the features for hiking
        pathSource.current?.addFeatures([...hikingLineFeatures, ...hikingEndcapFeatures]);
        hikingIconSource.current?.addFeatures([...hikingIconFeatures]);
        // Add all the features for running
        pathSource.current?.addFeatures([...runningLineFeatures, ...runningEndcapFeatures]);
        runningIconSource.current?.addFeatures([...runningIconFeatures]);
        // Add all the features for riding
        pathSource.current?.addFeatures([...ridingLineFeatures, ...ridingEndcapFeatures]);
        ridingIconSource.current?.addFeatures([...ridingIconFeatures]);
        // Add all the features for canoe
        pathSource.current?.addFeatures([...canoeLineFeatures, ...canoeEndcapFeatures]);
        canoeIconSource.current?.addFeatures([...canoeIconFeatures]);
        // Add all the features for chair lift
        pathSource.current?.addFeatures([...chairLiftLineFeatures, ...chairLiftEndcapFeatures]);
        chairLiftIconSource.current?.addFeatures([...chairLiftIconFeatures]);
        // Add all the features for gondola
        pathSource.current?.addFeatures([...gondolaLineFeatures, ...gondolaEndcapFeatures]);
        gondolaIconSource.current?.addFeatures([...gondolaIconFeatures]);
        // Add all the features for surface lift
        pathSource.current?.addFeatures([...surfaceLiftLineFeatures, ...surfaceLiftEndcapFeatures]);
        surfaceLiftIconSource.current?.addFeatures([...surfaceLiftIconFeatures]);
        // Add all the features for belt lift
        pathSource.current?.addFeatures([...beltLiftLineFeatures, ...beltLiftEndcapFeatures]);
        beltLiftIconSource.current?.addFeatures([...beltLiftIconFeatures]);

        // Add all the features for ski slope
        pathSource.current?.addFeatures([...skiSlopeLineFeatures, ...skiSlopeEndcapFeatures]);
        skiSlopeIconSource.current?.addFeatures([...skiSlopeIconFeatures]);
    }

    /**
     * Draws the required features for a path to the provided feature lists
     */
    const drawPath = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        endcapFeatures: Feature<Geometry>[],
        path: Path,
        isBold: boolean = false,
        direction?: PathDirection,
    ) => {
        if (!pathCategoryProps.current) {
            return;
        }

        if (!pathGpsData.current || !pathGpsData.current[path.id]) {
            return;
        }
        const { difficulty, pathCategoryId } = path;
        
        // Get the difficulty color for the ski slope path, use cross-country skiing color for all other paths
        const color = pathCategoryId === PathCategoryId.SkiSlope
            ? PROPS.PATHS.DIFFICULTY[difficulty]
            : COLORS.PATHS.CROSS_COUNTRY_SKIING;

        const {icon, closedIcon} = pathCategoryProps.current[path.pathCategoryId];
        const lineCoordinates = pathGpsData.current[path.id].map((coordinate: PathGpsCoordinate, index: number) =>
            fromLonLat([coordinate.longitude, coordinate.latitude, index]),
        );
        
        // Skip if no coordinates
        if (!lineCoordinates || lineCoordinates.length === 0) {
            return;
        }
        
        // Get status directly from path object (it's an enum, not a translated property)
        const pathStatus = path.status;
        const iconUrl = pathStatus !== PathStatus.Closed ? icon : closedIcon;
        
        // Add line features (path and arrows)
        lineFeatures.push(
            ...createCommonPathFeatures(
                lineCoordinates,
                path.id,
                color,
                pathStatus === PathStatus.Closed,
                isBold,
                direction,
            ),
        );
        
        // Add path type icons along the trail (always show, even for closed paths)
        const iconOpacity = pathStatus === PathStatus.Closed ? 0.5 : 1;
        iconFeatures.push(...createPathIconFeatures(lineCoordinates, iconUrl, color, 1000, iconOpacity));

        const endIndex = lineCoordinates.length - 1;
      
        const hasDarkBackground = mapType.current === MapType.Satelite;
        const startName = getTranslatedProperty(selectedLanguage?.current, 'start', path)
        const endName = getTranslatedProperty(selectedLanguage?.current, 'end', path)
        const endcapOpacity = pathStatus === PathStatus.Closed ? 0.3 : 1;
        endcapFeatures.push(createEndcapIconFeature(new Point(lineCoordinates[0]), startName, hasDarkBackground, color, endcapOpacity));
        endcapFeatures.push(createEndcapIconFeature(new Point(lineCoordinates[endIndex]), endName, hasDarkBackground, color, endcapOpacity));
    };

    /**
     * Draws all the paths provided in snowmobilePathData, filter with optional ID array
     */
    const drawSnowmobilePaths = (ids?: string[]) => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        if (ids?.length) {
            ids.forEach((id) => {
                const path = snowmobilePathData.current[id];
        
                path.subPaths.forEach((subPath) => {
                    if (typeof subPath === 'string') {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, snowmobileSubPathData.current[subPath]);
                    }
                    else {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                    }
                });
            });
        } else {
            Object.values(snowmobileSubPathData.current).forEach((subPath) => {
                drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
            });
        }
      
        snowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
        snowmobileIconSource.current?.addFeatures([]); // NOTE: Empty at the moment as many paths icons for crossings overlap, resulting in always being clustered
    };

    /**
     * Draws all the subpaths provided in snowmobileSubPathData, filter with optional ID array
     */
    const drawSnowmobileSubPaths = (ids?: string[]) => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        if (ids?.length) {
            Object.values(snowmobileSubPathData.current).forEach((subPath) => {
                if (ids.includes(subPath.id)) {
                    drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                }
            });
        }
      
        snowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
        snowmobileIconSource.current?.addFeatures([]); // NOTE: Empty at the moment as many paths icons for crossings overlap, resulting in always being clustered
    };

    /**
     * Draws the required features for a snowmobile subpath to the provided feature lists
     */
    const drawSnowmobileSubPath = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        subPath: SnowmobileSubPath,
        isBold: boolean = false,
    ) => {
        const lineCoordinates = subPath.subPathGPSCoordinates.map((coordinate) =>
            fromLonLat([coordinate.longitude, coordinate.latitude]),
        );
        
        // Skip if no coordinates
        if (!lineCoordinates || lineCoordinates.length === 0) {
            return;
        }
      
        lineFeatures.push(
            ...createSnowmobilePathFeatures(
                lineCoordinates,
                subPath.id,
                subPath.status === PathStatus.Closed,
                isBold,
            ),
        );
      
        if (subPath.start) {
            iconFeatures.push(
                createSnowmobilingIconFeature(new Point(lineCoordinates[0]), subPath.start, isBold),
            );
        }
      
        if (subPath.end) {
            const endIndex = lineCoordinates.length - 1;
            iconFeatures.push(
                createSnowmobilingIconFeature(new Point(lineCoordinates[endIndex]), subPath.end, isBold),
            );
        }
    };

    /**
     * Draws all the POIs provided in poiData, filter with optional ID array
     */
    const drawPois = (ids?: string[]) => {
        const iconFeatures: Feature<Geometry>[] = [];

        Object.values(poiData.current).forEach((poi) => {
            if (!ids?.length || ids.includes(poi.id)) {
                drawPoi(iconFeatures, poi);
            }
        });
        
        poiSource.current?.addFeatures([...iconFeatures]);
    };

    /**
     * Draws the required features for a POI to the provideed feature lists
     */
    const drawPoi = (iconFeatures: Feature<Geometry>[], poi: Poi, isBold: boolean = false) => {
        if (!poiCategoryProps.current) {
            return;
        }
        
        // Skip unknown POI categories gracefully
        const categoryProps = poiCategoryProps.current[poi.poiCategoryId];
        if (!categoryProps) {
            console.warn(`Skipping POI ${poi.id}: unknown category ${poi.poiCategoryId}`);
            return;
        }
        
        const {color, icon} = categoryProps;
        const coordinates = fromLonLat([poi.longitude, poi.latitude]);
      
        const hasDarkBackground = mapType.current === MapType.Satelite;
        
        // Check if this is a temperature POI (POI_CATEGORY_19)
        // poiCategoryId is a string after conversion from API response
        if (poi.poiCategoryId === '19') {
            // Extract temperature from POI description
            // Description might be a string (JSON) or an object
            let temperature = 0;
            
            if (typeof poi.description === 'string') {
                try {
                    const parsed = JSON.parse(poi.description);
                    temperature = parsed?.temperature ?? 0;
                } catch (e) {
                    temperature = 0;
                }
            } else if (typeof poi.description === 'object' && poi.description !== null) {
                temperature = (poi.description as any)?.temperature ?? 0;
            }
            
            const tempFeature = createTemperaturePoiIconFeature(new Point(coordinates), poi.id, temperature, color, icon, hasDarkBackground, isBold);
            tempFeature.set('__isTemperature', true); // Mark as temperature POI for zoom handling
            iconFeatures.push(tempFeature);
        } else {
            const name = getTranslatedProperty(selectedLanguage?.current, 'name', poi)
            iconFeatures.push(
                createCommonPoiIconFeature(new Point(coordinates), poi.id, name, color, icon, hasDarkBackground, isBold),
            );
        }
    };

    /**
     * Draws all the AOIs provided in aoiData, filter with optional ID array
     */
    const drawAois = (ids?: string[]) => {
        const iconFeatures: Feature<Geometry>[] = [];
        const lineFeatures: Feature<Geometry>[] = [];    

        Object.values(aoiData.current).forEach((aoi) => {
            if (!ids?.length || ids.includes(aoi.id)) {
                drawAoi(lineFeatures, iconFeatures, aoi);
            }
        });
        
        aoiSource.current?.addFeatures([...iconFeatures]);
    };

    /**
     * Draws the required features for a aoi to the provided feature lists
     */
    const drawAoi = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        aoi: Aoi,
        isBold: boolean = false,
    ) => {

        if (!aoiCategoryProps.current) {
            return;
        }

        if (!aoiGpsData.current || !aoiGpsData.current[aoi.id]) {
            return;
        }
        
        // Skip unknown AOI categories gracefully
        const categoryProps = aoiCategoryProps.current[aoi.aoiCategoryId];
        if (!categoryProps) {
            console.warn(`Skipping AOI ${aoi.id}: unknown category ${aoi.aoiCategoryId}`);
            return;
        }

        const {color, icon} = categoryProps;
        const lineCoordinates = aoiGpsData.current[aoi.id].map((coordinate: AoiGpsCoordinate, index: number) =>
            fromLonLat([coordinate.longitude, coordinate.latitude, index]),
        );
     
        lineFeatures.push(
            ...createCommonAoiFeatures(
                lineCoordinates,
                aoi.id,
                color,
            ),
        );

        const xCoordinates = lineCoordinates.map((coordinate) => coordinate[0]);
        xCoordinates.sort((a, b) => a - b);
        const yCoordinates = lineCoordinates.map((coordinate) => coordinate[1]);
        yCoordinates.sort((a, b) => a - b);
        const center = [
          (xCoordinates[0] + xCoordinates[xCoordinates.length - 1]) / 2,
          (yCoordinates[yCoordinates.length - 1] + yCoordinates[0]) / 2,
        ];

        const hasDarkBackground = mapType.current === MapType.Satelite;
        const name = getTranslatedProperty(selectedLanguage?.current, 'name', aoi);
        iconFeatures.push(
            createCommonAoiIconFeature(new Point(center), aoi.id, name, color, icon, hasDarkBackground, isBold),
        );
    };

    /**
     * Draws the required features for the current selection to the highlight source
     */
    const redrawHighlight = () => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        highlightedPathSource.current?.clear();
        highlightedPathIconSource.current?.clear();
        highlightedSnowmobilePathSource.current?.clear();
        highlightedPoiSource.current?.clear();
        highlightedAoiSource.current?.clear();

        highlightedPathLayer.current?.setVisible(false);
        highlightedPathIconLayer.current?.setVisible(false);
        highlightedSnowmobilePathLayer.current?.setVisible(false);
        highlightedPoiLayer.current?.setVisible(false);
        highlightedAoiLayer.current?.setVisible(false);
        
        if (!highlightedId.current) {
            return;
        }
      
        switch (highlightedType.current) {
            case FeatureType.Path:
                const path = pathData.current[`${highlightedId.current}`];
                const endcapFeatures: Feature<Geometry>[] = [];
                drawPath(lineFeatures, iconFeatures, endcapFeatures, path, true, pathDirection.current);
                highlightedPathSource.current?.addFeatures([...lineFeatures, ...endcapFeatures]);
                highlightedPathIconSource.current?.addFeatures(iconFeatures);
                highlightedPathLayer.current?.setVisible(true);
                highlightedPathIconLayer.current?.setVisible(true);
                break;
        
            case FeatureType.SnowmobilePath:
                const snowmobilePath = snowmobilePathData.current[`${highlightedId.current}`];
                snowmobilePath.subPaths.forEach((subPath) => {
                    if (typeof subPath === 'string') {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, snowmobileSubPathData.current[subPath], true);
                    }
                    else {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                    }
                });
                highlightedSnowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedSnowmobilePathLayer.current?.setVisible(true);
                break;
        
            case FeatureType.SnowmobileSubPath:
                const snowmobileSubPath = snowmobileSubPathData.current[highlightedId.current];
                drawSnowmobileSubPath(
                    lineFeatures,
                    iconFeatures,
                    snowmobileSubPath,
                    true,
                );
                highlightedSnowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedSnowmobilePathLayer.current?.setVisible(true);
                break;
        
            case FeatureType.Poi:
                const poi = poiData.current[`${highlightedId.current}`];
                drawPoi(iconFeatures, poi, true);
                highlightedPoiSource.current?.addFeatures(iconFeatures);
                highlightedPoiLayer.current?.setVisible(true);
                break;

            case FeatureType.Aoi:
                const aoi = aoiData.current[`${highlightedId.current}`];
                drawAoi(lineFeatures, iconFeatures, aoi, true);
                highlightedAoiSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedAoiLayer.current?.setVisible(true);
                break;
        
            default:
                break;
        }
    };

    /**
     * Recenters the selected feature, recenters all content if no selection is provided
     */
    const refocus = () => {
        console.log('[MAP DEBUG] refocus() called', {
            selectedType: selectedType.current,
            selectedId: selectedId.current,
            focusData: focusData.current,
            filterData: filterData.current,
            stack: new Error().stack?.split('\n').slice(1, 5).join('\n')
        });
        const source = getSourceForType(selectedType.current);
        const POI_MIN_ZOOM = 13; // Same as defined for POI visibility
        
        // Check if we're focusing on POIs (from filtered list "show on map")
        // Check both focusData and filterData since filters are used for "show on map"
        const dataToCheck = focusData.current?.length ? focusData.current : filterData.current;
        const hasPois = dataToCheck?.some(group => group.type === FeatureType.Poi);
        
        const options: FitOptions = {
            padding: [100, 100, 100, 100],
            maxZoom: selectedType.current === FeatureType.Poi ? 16 : 16,
        };

        if (source?.getFeatures().length) {
            if (selectedId.current !== undefined) {
                let extent = createEmpty();
        
                if (selectedType.current === FeatureType.SnowmobilePath) {
                    const subPathIds = snowmobilePathData.current[selectedId.current]?.subPaths;
            
                    if (subPathIds?.length) {
                        source.getFeatures().forEach((feature) => {
                            subPathIds.forEach((subPathId) => {
                                const type = feature.get(OBJECT_TYPE);
                                const id = feature.get(OBJECT_ID);

                                if (type === FeatureType.SnowmobileSubPath && id === subPathId) {
                                    extend(extent, feature!.getGeometry()!.getExtent());
                                }
                            });
                        });
                    }
                } else {
                    source.getFeatures().forEach((feature) => {
                        const type = feature.get(OBJECT_TYPE);
                        const id = feature.get(OBJECT_ID);
                    
                        if (type === selectedType.current && id === selectedId.current) {
                            extend(extent, feature!.getGeometry()!.getExtent());
                        }
                    });
                }

                map.current?.getView().fit(extent, options);
            } else {
                const extent = source.getExtent();
                map.current?.getView().fit(extent, options);
            }
        } else {
            const extent = createEmpty();
            if (pathSource.current && snowmobilePathSource.current && poiSource.current && aoiSource.current) {
                // Use focusData if available, otherwise use filterData
                const dataToUse = focusData.current?.length ? focusData.current : filterData.current;
                
                if (dataToUse?.length) {
                    dataToUse.forEach((group) => {
                        const {type: groupType, ids} = group;
                        const groupSource = getSourceForType(groupType);

                        groupSource?.getFeatures().forEach((feature) => {
                            const type = feature.get(OBJECT_TYPE);
                            const id = feature.get(OBJECT_ID);
                        
                            if (type === groupType && ids.includes(id)) {
                                extend(extent, feature!.getGeometry()!.getExtent());
                            }
                        });
                    });
                }
                else {
                    extend(extent, pathSource.current.getExtent());
                    extend(extent, snowmobilePathSource.current.getExtent());
                    extend(extent, poiSource.current.getExtent());
                    extend(extent, aoiSource.current.getExtent());
                }
            }
            if (!isEmpty(extent)) {
                map.current?.getView().fit(extent, options);
            }
        }
        
        // If we're focusing on POIs and zoom is too far out, adjust to POI_MIN_ZOOM
        // This ensures POIs are always visible after "show on map"
        if (hasPois) {
            setTimeout(() => {
                const view = map.current?.getView();
                const currentZoom = view?.getZoom();
                if (currentZoom !== undefined && currentZoom < POI_MIN_ZOOM) {
                    view?.setZoom(POI_MIN_ZOOM);
                }
            }, 100);
        }
    };

    /**
     * Gets the source for a given feature type
     */
    const getSourceForType = (type?: string): VectorSource<Feature<Geometry>> | undefined => {
        switch (type) {
            case FeatureType.Path:
                return pathSource.current;
        
            case FeatureType.SnowmobilePath:
            case FeatureType.SnowmobileSubPath:
                return snowmobilePathSource.current;
        
            case FeatureType.Poi:
                return poiSource.current;

            case FeatureType.Aoi:
                return aoiSource.current;
        
            default:
                return undefined;
        }
    };

    /**
     * Gets the distance between two coordinates
     */
    const getCoordsDistance = (coordinate1: number[], coordinate2: number[], projection?: string): number => {
        projection = projection || 'EPSG:4326';
    
        const sourceProj = map.current?.getView().getProjection();
        const c1 = transform(coordinate1, sourceProj, projection);
        const c2 = transform(coordinate2, sourceProj, projection);
    
        return getDistance(c1, c2);
    };

    /**
     * Posts a message about the feature nearest to a GPS coordinate
     */
    const reportNearestFeature = (coordinate: number[]) => {
        const closestFeature = pathSource.current?.getClosestFeatureToCoordinate(coordinate);

        if (closestFeature) {
            const closestPoint = closestFeature!
                .getGeometry()!
                .getClosestPoint(coordinate);

            if (getCoordsDistance(closestPoint, coordinate) > 70) {
                return;
            }

            const objectId = closestFeature.get(OBJECT_ID);
            if (!objectId) {
                return;
            }

            const latLongCoords = toLonLat(closestPoint);
            const msg = JSON.stringify({
                pathId: parseInt(objectId, 10),
                pointCoordinate: {
                    x: latLongCoords[0],
                    y: latLongCoords[1],
                    ...(closestPoint[2] && {index: closestPoint[2]}),
                },
            });
            postMessage(msg);
            onMessage?.(msg);
        } else {
            const msg = JSON.stringify({
                pathId: 0,
                pointCoordinate: {
                    x: 0,
                    y: 0,
                },
            });
            postMessage(msg);
            onMessage?.(msg);
        }
    };

    return (
        <>
            <div ref={mapRef} className="map"></div>
            <div ref={map3dRef} className="map map-3d" style={{display: 'none'}}></div>
        </>
    )
};

export default forwardRef(MapComponent);